/**
 * One day of the range calendar: a filled circle at either end of the run, a
 * soft band through the days between, and a dot under today.
 */
import { formatDateKey, type DateKey } from './date-keys';

export const CALENDAR_CELL = 36;

export interface CalendarDayProps {
  day: DateKey;
  inBand: boolean;
  endpoint: boolean;
  roundLeft: boolean;
  roundRight: boolean;
  disabled: boolean;
  isToday: boolean;
  onPick: (day: DateKey) => void;
  onHover: (day: DateKey) => void;
}

function bandRadius(roundLeft: boolean, roundRight: boolean): string {
  const l = roundLeft ? 18 : 0;
  const r = roundRight ? 18 : 0;
  return `${l}px ${r}px ${r}px ${l}px`;
}

function dayColor(endpoint: boolean, disabled: boolean): string {
  if (endpoint) return 'var(--nb-on-primary)';
  return disabled ? 'var(--nb-faint)' : 'var(--nb-ink)';
}

export function CalendarDay(p: CalendarDayProps) {
  return (
    <div
      style={{
        height: CALENDAR_CELL,
        display: 'flex',
        justifyContent: 'center',
        background: p.inBand ? 'var(--nb-primary-soft)' : 'transparent',
        borderRadius: bandRadius(p.roundLeft, p.roundRight),
      }}
    >
      <button
        type="button"
        disabled={p.disabled}
        aria-pressed={p.endpoint}
        aria-label={formatDateKey(p.day)}
        aria-current={p.isToday ? 'date' : undefined}
        onClick={() => p.onPick(p.day)}
        onMouseEnter={() => p.onHover(p.day)}
        style={{
          width: CALENDAR_CELL,
          height: CALENDAR_CELL,
          borderRadius: 999,
          position: 'relative',
          fontSize: 13,
          fontWeight: p.endpoint || p.isToday ? 800 : 500,
          background: p.endpoint ? 'var(--nb-primary)' : 'transparent',
          color: dayColor(p.endpoint, p.disabled),
          cursor: p.disabled ? 'default' : 'pointer',
          opacity: p.disabled ? 0.55 : 1,
        }}
      >
        {Number(p.day.slice(8))}
        {p.isToday && (
          <i
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: '50%',
              bottom: 5,
              width: 4,
              height: 4,
              marginLeft: -2,
              borderRadius: 999,
              background: p.endpoint ? 'var(--nb-on-primary)' : 'var(--nb-primary)',
            }}
          />
        )}
      </button>
    </div>
  );
}
